import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';

const STATUS_COLOR = { pending:'#e67e22', approved:'#2ecc71', rejected:'#e74c3c', cancelled:'#888' };
const LEAVE_TYPES  = { annual:'Annual Leave', sick:'Sick Day', emergency:'Emergency Leave',
                       unpaid:'Unpaid Leave', maternity:'Maternity / Paternity', other:'Other' };

// ─── Approval Stamp SVG ───────────────────────────────────────────────────────
const STAMP_CFG_HR = {
  storekeeper: { label:'STOREKEEPER', scaleFont:0.85 },
  manager:     { label:'MANAGER',     scaleFont:1    },
  accountant:  { label:'ACCOUNTANT',  scaleFont:0.82 },
  director:    { label:'DIRECTOR',    scaleFont:0.9  },
  hr:          { label:'HR',          scaleFont:1.1  },
};
function ApprovalStampHR({ role, by, at, size=72 }) {
  const cfg = STAMP_CFG_HR[role] || { label:role.toUpperCase(), scaleFont:1 };
  const cx = size/2, cy = size/2;
  const rOuter = cx - 3, rInner = cx - 10;
  const arcR = rOuter - 5;
  const uid = `hr_stamp_${role}_${size}`;
  const mainFontSize = Math.round(9 * cfg.scaleFont * (size/72));
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={Math.max(5,size*0.077)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={mainFontSize}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" letterSpacing="0.3"
          filter={`url(#${uid}_d)`}>{cfg.label}</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at  && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

export default function HRPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab] = useState('leave');

  return (
    <div>
      <PageHeader icon="👥" title="Human Resources" sub="Leave management, staff accounts, payroll overview" />
      <TabBar tabs={[
        { id:'leave',    label:'Leave Requests', icon:'📅' },
        { id:'staff',    label:'Staff Accounts',  icon:'👤' },
        { id:'payroll',  label:'Payroll',          icon:'💰' },
      ]} active={tab} onChange={setTab} />

      {tab === 'leave'   && <LeaveManagementPanel notify={notify} />}
      {tab === 'staff'   && <StaffManagementPanel notify={notify} />}
      {tab === 'payroll' && <PayrollSummaryPanel  notify={notify} />}
    </div>
  );
}


// ─── Leave Management ─────────────────────────────────────────────────────────
function LeaveManagementPanel({ notify }) {
  const bp = useBreakpoint();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter]     = useState('all');
  const [selected, setSelected] = useState(null);
  const [hrNotes, setHrNotes]   = useState('');
  const [loading, setLoading]   = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.getLeaveRequests();
      setRequests(r.data.results || r.data);
    } catch(e) { notify('Failed to load leave requests', 'error'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const counts   = {
    all: requests.length,
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  const doApprove = async (lr) => {
    setLoading(true);
    try {
      await api.approveLeaveRequest(lr.id, { hr_notes: hrNotes });
      notify(`✅ Leave approved — ${lr.employee_name}'s account deactivated until return`);
      setSelected(null); setHrNotes('');
      await load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
    finally { setLoading(false); }
  };

  const doReject = async (lr) => {
    if (!hrNotes.trim()) { notify('Please provide a reason for rejection', 'error'); return; }
    setLoading(true);
    try {
      await api.rejectLeaveRequest(lr.id, { reason: hrNotes });
      notify(`Leave request rejected`);
      setSelected(null); setHrNotes('');
      await load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
    finally { setLoading(false); }
  };

  const doReactivate = async (lr) => {
    setLoading(true);
    try {
      await api.reactivateEmployee(lr.id);
      notify(`✅ ${lr.employee_name}'s account reactivated`);
      await load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
    finally { setLoading(false); }
  };

  const pill = (status) => (
    <span style={{ padding:'2px 10px', borderRadius:3, fontSize:11, fontWeight:700,
      background: STATUS_COLOR[status] + '22', color: STATUS_COLOR[status],
      textTransform:'capitalize', border:`1px solid ${STATUS_COLOR[status]}44` }}>
      {status}
    </span>
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : selected ? '1fr 400px' : '1fr', gap:16 }}>
      <div>
        {/* Summary stat cards */}
        <StatCards cards={[
          { label:'Pending',       value:counts.pending,  icon:'⏳', color:'#e67e22', sub:'Awaiting review' },
          { label:'Approved',      value:counts.approved, icon:'✅', color:'#2ecc71', sub:'This period' },
          { label:'Rejected',      value:counts.rejected, icon:'❌', color:'#e74c3c' },
          { label:'Total Requests',value:counts.all,      icon:'📋', color:'#3498db' },
        ]} />

        {/* Filter tabs */}
        <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
          {Object.entries(counts).map(([k,v]) => (
            <button key={k} onClick={() => setFilter(k)} style={{
              padding:'5px 14px', borderRadius:4, border:`1px solid ${filter===k?'#c8a96e':'var(--border)'}`,
              background: filter===k?'#c8a96e22':'var(--bgInput)', color:filter===k?'#c8a96e':'var(--textMuted)',
              fontSize:11, fontWeight:600, cursor:'pointer',
            }}>
              {k.charAt(0).toUpperCase()+k.slice(1)} ({v})
            </button>
          ))}
        </div>

        <Card title={`Leave Requests (${filtered.length})`}>
          {filtered.length === 0 ? <Empty text="No requests found" /> :
            filtered.map(lr => (
              <div key={lr.id} onClick={() => { setSelected(lr); setHrNotes(''); }}
                style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)',
                  cursor:'pointer', background: selected?.id===lr.id?'var(--bgHover)':'transparent',
                  transition:'background .15s' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div>
                    <div style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:13 }}>
                      {lr.employee_name}
                      <span style={{ marginLeft:8, fontSize:10, color:'var(--textMuted)',
                        textTransform:'capitalize', fontWeight:400 }}>
                        {lr.employee_role?.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--textSecond)', marginTop:2 }}>
                      {LEAVE_TYPES[lr.leave_type] || lr.leave_type} · {lr.days} day{lr.days!==1?'s':''}
                    </div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>
                      {lr.date_from} → {lr.date_to}
                    </div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2, fontStyle:'italic' }}>
                      "{lr.reason?.slice(0,80)}{lr.reason?.length>80?'…':''}"
                    </div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    {pill(lr.status)}
                    {lr.credentials_deactivated && (
                      <div style={{ fontSize:10, color:'#e74c3c', marginTop:4 }}>🔒 Account Locked</div>
                    )}
                  </div>
                </div>
              </div>
            ))
          }
        </Card>
      </div>

      {/* Detail / Action Panel */}
      {selected && (
        <Card title="Review Leave Request">
          <button style={{ ...btnSm, marginBottom:14, color:'var(--textMuted)' }}
            onClick={() => setSelected(null)}>✕ Close</button>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8, marginBottom:14 }}>
            {[
              ['Employee', selected.employee_name],
              ['Role', selected.employee_role?.replace('_',' ')],
              ['Leave Type', LEAVE_TYPES[selected.leave_type] || selected.leave_type],
              ['Days', `${selected.days} day${selected.days!==1?'s':''}`],
              ['From', selected.date_from],
              ['To', selected.date_to],
              ['Status', selected.status],
              ['Submitted', new Date(selected.created_at).toLocaleDateString('en-KE')],
            ].map(([label,val]) => (
              <div key={label} style={{ background:'var(--bgInput)', padding:'8px 10px', borderRadius:4 }}>
                <div style={{ fontSize:10, color:'var(--textMuted)' }}>{label}</div>
                <div style={{ fontWeight:600, color:'var(--textPrimary)', fontSize:12, textTransform:'capitalize' }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ background:'var(--bgInput)', padding:'10px 12px', borderRadius:4, marginBottom:14 }}>
            <div style={{ fontSize:10, color:'var(--textMuted)', marginBottom:4 }}>Reason</div>
            <div style={{ fontSize:12, color:'var(--textSecond)', lineHeight:1.5 }}>{selected.reason}</div>
          </div>

          {selected.hr_notes && (
            <div style={{ background:'var(--bgSuccess)', padding:'10px 12px', borderRadius:4, marginBottom:14,
              border:'1px solid #2ecc7133' }}>
              <div style={{ fontSize:10, color:'#2ecc71', marginBottom:4 }}>HR Notes</div>
              <div style={{ fontSize:12, color:'var(--textSecond)' }}>{selected.hr_notes}</div>
            </div>
          )}

          {selected.status === 'pending' && (
            <>
              <Field label="HR Notes / Reason (required for rejection)">
                <textarea style={{ ...inputStyle, minHeight:70 }} value={hrNotes}
                  onChange={e => setHrNotes(e.target.value)}
                  placeholder="Optional notes for approval, or reason if rejecting..." />
              </Field>
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button style={{ ...btnPrimary, flex:1, background:'linear-gradient(135deg,#27ae60,#1e8449)' }}
                  onClick={() => doApprove(selected)} disabled={loading}>
                  {loading ? '⏳…' : '✅ Approve (Locks Account)'}
                </button>
                <button style={{ ...btnPrimary, flex:1, background:'linear-gradient(135deg,#c0392b,#922b21)' }}
                  onClick={() => doReject(selected)} disabled={loading}>
                  {loading ? '⏳…' : '❌ Reject'}
                </button>
              </div>
            </>
          )}

          {selected.status === 'approved' && selected.credentials_deactivated && (
            <div>
              {/* HR Approval Stamp */}
              <div style={{
                display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap',
                padding:'12px 14px', marginBottom:12,
                background:'rgba(200,169,110,0.05)',
                border:'1px solid rgba(200,169,110,0.15)', borderRadius:6,
              }}>
                <div style={{ width:'100%', fontSize:10, color:'#666', letterSpacing:2, textTransform:'uppercase', marginBottom:2 }}>
                  Official Approval Stamp
                </div>
                <ApprovalStampHR
                  role="hr"
                  by={selected.reviewed_by_name || null}
                  at={selected.reviewed_at}
                  size={80}
                />
              </div>
              <div style={{ padding:'10px 12px', background:'#1a0a0a', border:'1px solid #e74c3c33',
                borderRadius:4, marginBottom:10, fontSize:12, color:'#e74c3c' }}>
                🔒 This employee's account is currently deactivated (on leave). Reactivate when they return.
              </div>
              <button style={{ ...btnPrimary, width:'100%', background:'linear-gradient(135deg,#2980b9,#1a5276)' }}
                onClick={() => doReactivate(selected)} disabled={loading}>
                {loading ? '⏳…' : '🔓 Reactivate Account (Employee Returned)'}
              </button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}


// ─── Staff Management (HR view — same as admin but HR-focused) ────────────────
function StaffManagementPanel({ notify }) {
  const [users, setUsers]   = useState([]);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try { const r = await api.getUsers(); setUsers(r.data.results || r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const doTerminate = async (u) => {
    if (!window.confirm(`Terminate ${u.name}? This will deactivate their account and log them out.`)) return;
    try {
      await api.terminateEmployee(u.id);
      notify(`${u.name} terminated — account deactivated`);
      await load();
    } catch(e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };

  const doReactivate = async (u) => {
    try {
      await api.updateUser(u.id, { is_active: true });
      notify(`${u.name} reactivated`);
      await load();
    } catch(e) { notify('Failed', 'error'); }
  };

  const ROLE_COLOR = {
    admin:'#c8a96e', waiter:'#3498db', cashier:'#2ecc71', kitchen:'#e67e22',
    chef:'#e74c3c', reception:'#9b59b6', gate:'#e74c3c', housekeeping:'#1abc9c',
    accountant:'#f39c12', gift_shop:'#e91e8c', restaurant_manager:'#00b300',
    storekeeper:'#1a9ba0', hr:'#8e44ad', marketing:'#e84393',
  };

  const filtered = users.filter(u =>
    !search || u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.role.includes(search.toLowerCase())
  );

  return (
    <Card title="All Staff Accounts">
      <input style={{ ...inputStyle, marginBottom:14, maxWidth:300 }}
        placeholder="Search by name, username or role…"
        value={search} onChange={e => setSearch(e.target.value)} />

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead><tr style={{ background:'var(--bgInput)' }}>
            {['Name','Username','Role','Status','Leave?','Action'].map(h => (
              <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700,
                color:'var(--textMuted)', fontSize:10, borderBottom:'1px solid var(--border)' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'10px 12px', fontWeight:600, color:'var(--textPrimary)' }}>{u.name}</td>
                <td style={{ padding:'10px 12px', color:'var(--textMuted)', fontFamily:'monospace' }}>@{u.username}</td>
                <td style={{ padding:'10px 12px' }}>
                  <span style={{ padding:'2px 8px', borderRadius:3, fontSize:10, fontWeight:700,
                    background:(ROLE_COLOR[u.role]||'#555')+'22', color:ROLE_COLOR[u.role]||'#555',
                    textTransform:'capitalize' }}>
                    {u.role.replace('_',' ')}
                  </span>
                </td>
                <td style={{ padding:'10px 12px' }}>
                  <span style={{ color: u.is_active ? '#2ecc71' : '#e74c3c', fontWeight:700, fontSize:12 }}>
                    {u.is_active ? '● Active' : '● Inactive'}
                  </span>
                </td>
                <td style={{ padding:'10px 12px', fontSize:11, color:'var(--textMuted)' }}>
                  {/* Leave days used this year */}—
                </td>
                <td style={{ padding:'10px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    {u.role !== 'admin' && u.is_active && (
                      <button style={{ ...btnSm, background:'#5c1a1a', fontSize:10 }}
                        onClick={() => doTerminate(u)}>
                        Terminate
                      </button>
                    )}
                    {!u.is_active && u.role !== 'admin' && (
                      <button style={{ ...btnSm, background:'var(--bgSuccess)', fontSize:10, color:'#2ecc71' }}
                        onClick={() => doReactivate(u)}>
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


// ─── Payroll Summary ──────────────────────────────────────────────────────────
function PayrollSummaryPanel({ notify }) {
  return (
    <Card title="💰 Payroll Overview">
      <div style={{ padding:'20px', textAlign:'center', color:'var(--textMuted)', fontSize:13 }}>
        <div style={{ fontSize:32, marginBottom:12 }}>💼</div>
        <div style={{ fontWeight:600, color:'var(--textPrimary)', marginBottom:8 }}>Payroll Module</div>
        <div style={{ marginBottom:16 }}>Full payroll management is accessible from the Accounting tab.</div>
        <div style={{ background:'var(--bgInput)', padding:'12px 16px', borderRadius:6,
          fontSize:11, color:'var(--textSecond)', textAlign:'left', maxWidth:400, margin:'0 auto' }}>
          <div style={{ fontWeight:700, color:'#c8a96e', marginBottom:8 }}>HR Payroll Responsibilities:</div>
          <div>• Review and submit payroll for accountant approval</div>
          <div>• Manage allowances, deductions, and bonuses</div>
          <div>• Process terminated employee final pay</div>
          <div>• Leave impact on monthly salary calculations</div>
        </div>
      </div>
    </Card>
  );
}
